import React, { useState, useEffect } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { X } from "lucide-react";
import { getDashboardTourStatusService, updateDashboardTourStatusService } from "../../../services/users.services";

// Custom Tooltip Component
const CustomTooltip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    isLastStep,
    size,
    skipProps,
}) => {
    return (
        <div
            {...tooltipProps}
            className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-[380px] relative font-sans border border-gray-100"
            style={{
                zIndex: 10000,
            }}
        >
            {/* Header: Step Info & Close Button */}
            <div className="flex justify-between items-start mb-3">
                <span className="text-gray-500 text-xs font-medium">
                    Step {index + 1} of {size}
                </span>
                <button
                    {...closeProps}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Content */}
            <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                    {step.title}
                </h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">
                    {step.content}
                </p>
            </div>

            {/* Footer Navigation */}
            <div className="flex justify-between items-center mt-4 pt-2">
                {/* Skip Button */}
                <button
                    {...skipProps}
                    className="text-slate-500 text-sm font-medium hover:text-slate-800 transition-colors"
                >
                    Skip
                </button>

                <div className="flex items-center gap-3">
                    {/* Back Button (only if not first step) */}
                    {index > 0 && (
                        <button
                            {...backProps}
                            className="text-blue-600 text-sm font-semibold hover:text-blue-800 transition-colors px-3 py-2 rounded-lg hover:bg-blue-50"
                        >
                            Back
                        </button>
                    )}

                    {/* Next/Finish Button */}
                    <button
                        {...primaryProps}
                        className="bg-blue-600 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg active:scale-95"
                    >
                        {isLastStep ? "Finish" : "Next"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const DashboardTour = ({ start }) => {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [tourLoaded, setTourLoaded] = useState(false);

    useEffect(() => {
        const fetchTourStatus = async () => {
            try {
                const userId = localStorage.getItem("user_id");
                if (!userId) return;

                console.log("DashboardTour: Fetching status...");
                const response = await getDashboardTourStatusService();
                console.log("DashboardTour: Response:", response);

                const { last_visited_step_index, is_tour_completed, is_tour_skipped } = response;

                // Ensure we handle 0 values correctly as falsy
                const completed = is_tour_completed === 1 || is_tour_completed === true;
                const skipped = is_tour_skipped === 1 || is_tour_skipped === true;

                console.log("DashboardTour: Status:", { completed, skipped, last_visited_step_index });

                if (!completed && !skipped) {
                    setStepIndex(Number(last_visited_step_index) || 0);
                    // Mark as ready to run, but wait for 'start' prop
                    setTourLoaded(true);
                    console.log("DashboardTour: Tour loaded, waiting for start...");
                } else {
                    setTourLoaded(false); // Tour shouldn't run
                    console.log("DashboardTour: Tour completed or skipped.");
                }
            } catch (error) {
                console.error("Error fetching dashboard tour status:", error);
                setStepIndex(0);
                setTourLoaded(true);
            }
        };

        fetchTourStatus();
    }, []); // Run once on mount

    useEffect(() => {
        console.log("DashboardTour: Effect check:", { start, tourLoaded });
        if (start && tourLoaded) {
            console.log("DashboardTour: Starting tour in 1.5s...");
            const timer = setTimeout(() => {
                setRun(true);
                console.log("DashboardTour: Tour running!");
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [start, tourLoaded]);

    // Listen for external restart signal (fired by TopBar info button)
    useEffect(() => {
        const handleRestart = () => {
            console.log("DashboardTour: Restart event received");
            setRun(false);
            setStepIndex(0);
            setTourLoaded(true);
            // Small delay so Joyride re-mounts cleanly
            setTimeout(() => setRun(true), 500);
        };
        window.addEventListener('restartDashboardTour', handleRestart);
        return () => window.removeEventListener('restartDashboardTour', handleRestart);
    }, []);

    const updateBackendStatus = async (index, status) => {
        try {
            await updateDashboardTourStatusService({
                step_index: index,
                status: status,
            });
        } catch (error) {
            console.error("Error updating dashboard tour status:", error);
        }
    };

    const handleJoyrideCallback = (data) => {
        const { action, index, status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        console.log("DashboardTour: Joyride Callback:", { type, status, action, index });

        // Handle Close/Cancel button explicitly
        if (action === ACTIONS.CLOSE || status === STATUS.SKIPPED) {
            console.log("DashboardTour: Closed explicitly via Close button or Skipped");
            setRun(false);
            updateBackendStatus(index, STATUS.SKIPPED);
            return;
        }

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            if (type === EVENTS.TARGET_NOT_FOUND) {
                console.warn(`DashboardTour: Target not found for step ${index}`);
            }
            // Update local state to approriate step
            // For controlled tour, we must update stepIndex
            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            setStepIndex(nextIndex);
            updateBackendStatus(nextIndex, 'active');
        } else if (finishedStatuses.includes(status)) {
            console.log("DashboardTour: Finished/Skipped", status);
            setRun(false);
            updateBackendStatus(index, status);
        }
    };

    const steps = [
        {
            target: "#tour-current-program",
            title: "Program Selection",
            content:
                "This is your active study space. If you are enrolled in multiple programs, you can view and manage them all from this menu.",
            disableBeacon: true,
        },
        {
            target: "#tour-continue-learning",
            title: "Continue Learning",
            content:
                "Jump straight back into your academic corner with a single click.",
            disableBeacon: true,
        },
        {
            target: "#tour-go-to-lms",
            title: "Go To LMS",
            content:
                "This is your portal to all academic resources, including recordings and reading materials.",
            disableBeacon: true,
        },
        {
            target: "#tour-event-calendar",
            title: "Event Calendar",
            content:
                "Never miss your scheduled sessions. Check here for upcoming sessions and masterclasses.",
            disableBeacon: true,
        },
        {
            target: "#tour-career-counselling",
            title: "Career Counselling",
            content:
                "Schedule 1-on-1 sessions with experts to fast-track your professional growth.",
            disableBeacon: true,
        },
        {
            target: "#tour-dashboard-feedback",
            title: "Share Feedback",
            content:
                "We value your input! Share your thoughts and suggestions to help us improve your experience.",
            disableBeacon: true,
        },
    ];

    // if (!tourLoaded) {
    //     // console.log("DashboardTour: Tour not loaded or completed/skipped. Returning null.");
    //     return null;
    // }

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showProgress
            showSkipButton
            tooltipComponent={CustomTooltip}
            callback={handleJoyrideCallback}
            scrollOffset={500}
            debug={true}
            floaterProps={{
                disableAnimation: true,
            }}
            styles={{
                options: {
                    zIndex: 10000,
                    overlayColor: "rgba(0, 0, 0, 0.5)",
                },
            }}
        />
    );
};

export default DashboardTour;
